import type { CardType, ActionType as GameplayActionType, GameMode, Currency, Variant, HandType, Hand, LowHand, PotTransfer, PotShare, PlayerPayOut } from '@vgw/pok-gameplay-protocol'
import type { Card, ActionClock } from './PlayerGameState'
import type { DomainEvent, HandContext, HandPhase } from './DomainEvent'

export enum PlayerGameActionTypes {
	INITIALIZE_GAME = 'PLAYER_GAMES/INITIALIZE_GAME',
	UPDATE_HAND_STATE = 'PLAYER_GAMES/UPDATE_HAND_STATE',
	UPDATE_HOLE_CARDS = 'PLAYER_GAMES/UPDATE_HOLE_CARDS',
	UPDATE_COMMUNITY_CARDS = 'PLAYER_GAMES/UPDATE_COMMUNITY_CARDS',
	UPDATE_PLAYER_ACTION = 'PLAYER_GAMES/UPDATE_PLAYER_ACTION',
	UPDATE_PLAYER_BALANCE = 'PLAYER_GAMES/UPDATE_PLAYER_BALANCE',
	UPDATE_PLAYER_INFO = 'PLAYER_GAMES/UPDATE_PLAYER_INFO',
	REMOVE_PLAYER = 'PLAYER_GAMES/REMOVE_PLAYER',
	UPDATE_MY_SEAT = 'PLAYER_GAMES/UPDATE_MY_SEAT',
	UPDATE_TABLE_INFO = 'PLAYER_GAMES/UPDATE_TABLE_INFO',
	UPDATE_HAND_STRENGTH = 'PLAYER_GAMES/UPDATE_HAND_STRENGTH',
	HAND_ENDED = 'PLAYER_GAMES/HAND_ENDED',
	UPDATE_TOURNAMENT_INFO = 'PLAYER_GAMES/UPDATE_TOURNAMENT_INFO',
	RECORD_HAND_EVENT = 'PLAYER_GAMES/RECORD_HAND_EVENT',
}

export interface InitializeGamePayload {
	tableId: string
	mode: GameMode
	currency: Currency
	variant: Variant
	capacity: number
	blinds: {
		smallBlind: number
		bigBlind: number
		ante: number
	}
}

export interface UpdateHandStatePayload {
	tableId: string
	hand: {
		handId?: string
		dealerSeatId?: number
		phase?: HandPhase
		isMyTurn?: boolean
		activeSeat?: number
		actionClock?: ActionClock
	}
	context?: HandContext
}

export interface UpdateHoleCardsPayload {
	tableId: string
	cards: Record<number, (Card | null)[]>
}

export interface UpdateCommunityCardsPayload {
	tableId: string
	cards: Card[]
}

export interface UpdatePlayerActionPayload {
	tableId: string
	seatId: number
	action: GameplayActionType
	amount?: number
}

export interface UpdatePlayerBalancePayload {
	tableId: string
	seatId: number
	balance: number
}

export interface UpdatePlayerInfoPayload {
	tableId: string
	seatId: number
	playerId?: number
	name?: string
	isSittingIn?: boolean
	isAway?: boolean
	isDisconnected?: boolean
}

export interface HandEndedPayload {
	tableId: string
	hands: Record<number, Hand>
	lowHands: Record<number, LowHand>
	potTransfers: PotTransfer[]
	potShares: PotShare[]
	winningCards: CardType[]
	payOuts: PlayerPayOut[]
}

export interface UpdateTournamentInfoPayload {
	tableId: string
	tournamentInfo: {
		position?: number
		remainingPlayers?: number
		prize?: number
		isStarted?: boolean
		isEliminated?: boolean
		isFinished?: boolean
	}
}

export interface RecordHandEventPayload {
	tableId: string
	domainEvent: DomainEvent
}

export interface UpdateHandStrengthPayload {
	tableId: string
	seatId: number
	handType: HandType
}

export interface RemovePlayerPayload {
	tableId: string
	playerId: number
}

export interface UpdateMySeatPayload {
	tableId: string
	seatId: number
}

export interface UpdateTableInfoPayload {
	tableId: string
	tableInfo: {
		tableName?: string
		potSize?: number
		blinds?: {
			smallBlind: number
			bigBlind: number
			ante: number
		}
	}
}

export interface InitializeGameAction {
	type: PlayerGameActionTypes.INITIALIZE_GAME
	payload: InitializeGamePayload
}

export interface UpdateHandStateAction {
	type: PlayerGameActionTypes.UPDATE_HAND_STATE
	payload: UpdateHandStatePayload
}

export interface UpdateHoleCardsAction {
	type: PlayerGameActionTypes.UPDATE_HOLE_CARDS
	payload: UpdateHoleCardsPayload
}

export interface UpdateCommunityCardsAction {
	type: PlayerGameActionTypes.UPDATE_COMMUNITY_CARDS
	payload: UpdateCommunityCardsPayload
}

export interface UpdatePlayerActionAction {
	type: PlayerGameActionTypes.UPDATE_PLAYER_ACTION
	payload: UpdatePlayerActionPayload
}

export interface UpdatePlayerBalanceAction {
	type: PlayerGameActionTypes.UPDATE_PLAYER_BALANCE
	payload: UpdatePlayerBalancePayload
}

export interface UpdatePlayerInfoAction {
	type: PlayerGameActionTypes.UPDATE_PLAYER_INFO
	payload: UpdatePlayerInfoPayload
}

export interface RemovePlayerAction {
	type: PlayerGameActionTypes.REMOVE_PLAYER
	payload: RemovePlayerPayload
}

export interface UpdateMySeatAction {
	type: PlayerGameActionTypes.UPDATE_MY_SEAT
	payload: UpdateMySeatPayload
}

export interface UpdateTableInfoAction {
	type: PlayerGameActionTypes.UPDATE_TABLE_INFO
	payload: UpdateTableInfoPayload
}

export interface UpdateHandStrengthAction {
	type: PlayerGameActionTypes.UPDATE_HAND_STRENGTH
	payload: UpdateHandStrengthPayload
}

export interface HandEndedAction {
	type: PlayerGameActionTypes.HAND_ENDED
	payload: HandEndedPayload
}

export interface UpdateTournamentInfoAction {
	type: PlayerGameActionTypes.UPDATE_TOURNAMENT_INFO
	payload: UpdateTournamentInfoPayload
}

export interface RecordHandEventAction {
	type: PlayerGameActionTypes.RECORD_HAND_EVENT
	payload: RecordHandEventPayload
}

export type PlayerGameAction =
	| InitializeGameAction
	| UpdateHandStateAction
	| UpdateHoleCardsAction
	| UpdateCommunityCardsAction
	| UpdatePlayerActionAction
	| UpdatePlayerBalanceAction
	| UpdatePlayerInfoAction
	| RemovePlayerAction
	| UpdateMySeatAction
	| UpdateTableInfoAction
	| UpdateHandStrengthAction
	| HandEndedAction
	| UpdateTournamentInfoAction
	| RecordHandEventAction

export const initializeGame = (payload: InitializeGamePayload): InitializeGameAction => ({
	type: PlayerGameActionTypes.INITIALIZE_GAME,
	payload,
})

export const updateHandState = (payload: UpdateHandStatePayload): UpdateHandStateAction => ({
	type: PlayerGameActionTypes.UPDATE_HAND_STATE,
	payload,
})

export const updateHoleCards = (payload: UpdateHoleCardsPayload): UpdateHoleCardsAction => ({
	type: PlayerGameActionTypes.UPDATE_HOLE_CARDS,
	payload,
})

export const updateCommunityCards = (payload: UpdateCommunityCardsPayload): UpdateCommunityCardsAction => ({
	type: PlayerGameActionTypes.UPDATE_COMMUNITY_CARDS,
	payload,
})

export const updatePlayerAction = (payload: UpdatePlayerActionPayload): UpdatePlayerActionAction => ({
	type: PlayerGameActionTypes.UPDATE_PLAYER_ACTION,
	payload,
})

export const updatePlayerBalance = (payload: UpdatePlayerBalancePayload): UpdatePlayerBalanceAction => ({
	type: PlayerGameActionTypes.UPDATE_PLAYER_BALANCE,
	payload,
})

export const updatePlayerInfo = (payload: UpdatePlayerInfoPayload): UpdatePlayerInfoAction => ({
	type: PlayerGameActionTypes.UPDATE_PLAYER_INFO,
	payload,
})

export const removePlayer = (payload: RemovePlayerPayload): RemovePlayerAction => ({
	type: PlayerGameActionTypes.REMOVE_PLAYER,
	payload,
})

export const updateMySeat = (payload: UpdateMySeatPayload): UpdateMySeatAction => ({
	type: PlayerGameActionTypes.UPDATE_MY_SEAT,
	payload,
})

export const updateTableInfo = (payload: UpdateTableInfoPayload): UpdateTableInfoAction => ({
	type: PlayerGameActionTypes.UPDATE_TABLE_INFO,
	payload,
})

export const updateHandStrength = (payload: UpdateHandStrengthPayload): UpdateHandStrengthAction => ({
	type: PlayerGameActionTypes.UPDATE_HAND_STRENGTH,
	payload,
})

export const handEnded = (payload: HandEndedPayload): HandEndedAction => ({
	type: PlayerGameActionTypes.HAND_ENDED,
	payload,
})

export const updateTournamentInfo = (payload: UpdateTournamentInfoPayload): UpdateTournamentInfoAction => ({
	type: PlayerGameActionTypes.UPDATE_TOURNAMENT_INFO,
	payload,
})

export const recordHandEvent = (payload: RecordHandEventPayload): RecordHandEventAction => ({
	type: PlayerGameActionTypes.RECORD_HAND_EVENT,
	payload,
})
